import {
  Box,
  Stack,
  Divider,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";

// Components
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import ByzNurseDeleteButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseDeleteButton";
import ByzNursePrintButton2 from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton2";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import PatientDatas from "src/modules/medcard/patient-datas";
import { nurseService } from "src/api/nurse/nurseService";
import CardFooter from "../../shared/list/CardFooter";
import RequestServiceModal from "../../../../nurse/patient-list/pages/list/service-process/request-service/index";

// Local Components
import { PreliminaryEvaluation } from "./components/PreliminaryEvaluation";
import { AssessmentForm } from "./components/AssessmentForm";
import { MainAssessment } from "./components/MainAssessment";
import { EvaluationGrid } from "./components/EvaluationGrid";
import { InfoText } from "./components/InfoText";
import { EvaluationEditModal } from "./components/EvaluationEditModal";

// Hooks and Utilities
import { useTranslate } from "src/locales";
import { useCardForm } from "src/hooks/useCardForm";
import { useAppSelector } from "src/app/hooks";
import { referenceService } from "src/api/references/referenceService";
import { formatDateTime } from "src/utils/date";

// Models
import { NutritionalRiskScoreFormData } from "./models/types";

function NutritionalRiskScore({ patientId }: { readonly patientId?: any }) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const orId = Number(urlParams.get("orId")) || 2561;

  const effectivePatientId = patientId || id;
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslate();

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("error");

  const [localLoading, setLocalLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [evalDeleteConfirmOpen, setEvalDeleteConfirmOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | undefined>(undefined);
  const [selectedEvalId, setSelectedEvalId] = useState<number | null>(null);
  const [openRequestService, setOpenRequestService] = useState(false);

  const {
    formData,
    handleChange,
    resetForm,
    getTableName,
    getMCPParameterDetails,
    mcpCacheLoaded,
    loading: cardFormLoading,
  } = useCardForm({
    cardType: "MCNutritionalRiskScore",
    user,
    recordId: selectedRecordId,
  }) as any;

  const typedFormData = formData as NutritionalRiskScoreFormData;

  const [prevRecords, setPrevRecords] = useState<any[]>([]);
  const [evaluationRecords, setEvaluationRecords] = useState<any[]>([]);

  const getComId = useCallback(async (): Promise<number | null> => {
    try {
      const response = await referenceService.connectionsOfMedcard.getConnectionOfMedCard({
        pt: Number(effectivePatientId),
        orId,
      });

      const sorted = response.data?.slice().sort((a, b: any) => b.comid - a.comid) || [];
      let _comId = sorted[0]?.comid;

      if (!_comId) {
        const createResponse = await referenceService.connectionsOfMedcard.createConnectionOfMedCard({
          pt_id: Number(effectivePatientId),
          or_id: orId,
        });
        _comId = (createResponse as any).comid || (createResponse as any).data;
      }
      return _comId;
    } catch (error) {
      return null;
    }
  }, [effectivePatientId, orId]);

  const getMasterId = useCallback(async (comId: number): Promise<string | null> => {
    try {
      const response = await referenceService.card.getCardTableData({
        tableName: "MC_NUTRITIONAL_RISK_SCORE",
        columnName: "COM_ID",
        columnValue: comId,
      });
      if (response.data && response.data.length > 0) {
        return response.data[0].ID.toString();
      }
      const createResponse = await referenceService.card.post({
        tablename: "MC_NUTRITIONAL_RISK_SCORE",
        columns: [{ key: "COM_ID", value: String(comId) }],
      });
      const responseAny = createResponse as any;
      return responseAny.ID?.toString() || responseAny.data?.ID?.toString();
    } catch (error) {
      return null;
    }
  }, []);

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const calculateAge = useCallback((birthDate: string) => {
    if (!birthDate) return 0;
    const birth = new Date(birthDate);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      years--;
    }
    return years;
  }, []);

  const [patientData, setPatientData] = useState<any>(null);

  useEffect(() => {
    (async () => {
      if (!effectivePatientId) return;
      try {
        const data = await nurseService.patient.getPatientInfo({ id: Number(effectivePatientId) });
        setPatientData(data);
      } catch {
        setPatientData(null);
      }
    })();
  }, [effectivePatientId]);

  // Pre-fill form from patient data for new records (Add Evaluation button or initial empty state)
  useEffect(() => {
    if (patientData?.data && !typedFormData.ID) {
      const { patlength, weight: pWeight, birthdate } = patientData.data;

      if (patlength && Number(typedFormData.HEIGHT) === 0) {
        handleChange("HEIGHT", Number(patlength));
      }
      if (pWeight && Number(typedFormData.WEIGHT) === 0) {
        handleChange("WEIGHT", Number(pWeight));
      }
      if (birthdate) {
        const age = calculateAge(birthdate);
        const isOverSeventy = age >= 70 ? 1 : 0;
        if (typedFormData.OVER_SEVENTY !== isOverSeventy) {
          handleChange("OVER_SEVENTY", isOverSeventy);
        }
      }
    }
  }, [patientData, typedFormData.ID, typedFormData.HEIGHT, typedFormData.WEIGHT, typedFormData.OVER_SEVENTY, handleChange, calculateAge]);

  const fetchRecords = useCallback(async (silent = false) => {
    if (!effectivePatientId) return;
    if (!silent) setLocalLoading(true);
    try {
      const comId = await getComId();
      if (!comId) return;
      const masterId = await getMasterId(comId);
      if (!masterId) return;

      const response = await referenceService.card.getCardTableData({
        tableName: "MC_NUTRITIONAL_RISK_SCORE_DET",
        columnName: "NRS_ID",
        columnValue: Number(masterId)
      });
      if (response?.data) {
        setPrevRecords(response.data);
      }
    } catch (error) {
      // Silence error
    } finally {
      if (!silent) setLocalLoading(false);
      setIsInitialLoading(false);
    }
  }, [effectivePatientId, getComId, getMasterId]);

  const fetchEvaluationRecords = useCallback(async (detId: number) => {
    try {
      const response = await referenceService.card.getCardTableData({
        tableName: "MC_NUTRITIONAL_RISK_SCORE_ASS",
        columnName: "NRSD_ID",
        columnValue: detId
      });
      if (response?.data) {
        setEvaluationRecords(response.data);
      }
    } catch (error) {
      setEvaluationRecords([]);
    }
  }, []);

  useEffect(() => {
    if (selectedRecordId) {
      fetchEvaluationRecords(Number(selectedRecordId));
    } else {
      setEvaluationRecords([]);
    }
  }, [selectedRecordId, fetchEvaluationRecords]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    const bmi = Number(typedFormData.BODY_MASS_INDEX);
    const expectedScore = bmi > 0 && bmi < 20.5 ? 1 : 0;
    if (typedFormData.BODY_MASS_INDEX_SCORE !== expectedScore) {
      handleChange("BODY_MASS_INDEX_SCORE", expectedScore);
    }
  }, [typedFormData.BODY_MASS_INDEX, typedFormData.BODY_MASS_INDEX_SCORE, handleChange]);

  useEffect(() => {
    const height = Number(typedFormData.HEIGHT);
    const weight = Number(typedFormData.WEIGHT);
    if (height > 0 && weight > 0) {
      const hInMeters = height / 100;
      const bmi = weight / (hInMeters * hInMeters);
      const roundedBmi = Number.parseFloat(bmi.toFixed(2));
      if (typedFormData.BODY_MASS_INDEX !== roundedBmi) {
        handleChange("BODY_MASS_INDEX", roundedBmi);
      }
    }
  }, [typedFormData.HEIGHT, typedFormData.WEIGHT, typedFormData.BODY_MASS_INDEX, handleChange]);

  useEffect(() => {
    let nutritionScore = 0;
    if (Number(typedFormData.WEIGHT_LOSS_TREE_MONTHS) === 1) nutritionScore = 3;
    else if (Number(typedFormData.WEIGHT_LOSS_TWO_MONTHS) === 1) nutritionScore = 2;
    else if (Number(typedFormData.WEIGHT_LOSS_ONE_MONTHS) === 1) nutritionScore = 1;

    let diseaseScore = 0;
    if (Number(typedFormData.DISEASE_INTENSITY_TREE) === 1) diseaseScore = 3;
    else if (Number(typedFormData.DISEASE_INTENSITY_TWO) === 1) diseaseScore = 2;
    else if (Number(typedFormData.DISEASE_INTENSITY_ONE) === 1) diseaseScore = 1;

    const overSeventy = Number(typedFormData.OVER_SEVENTY) || 0;
    const totalScore = nutritionScore + diseaseScore + overSeventy;

    if (typedFormData.NUTRITIONAL_SCORE !== nutritionScore) handleChange("NUTRITIONAL_SCORE", nutritionScore);
    if (typedFormData.DISEASE_SCORE !== diseaseScore) handleChange("DISEASE_SCORE", diseaseScore);
    if (typedFormData.ASSESSMENT_SCORE !== totalScore) handleChange("ASSESSMENT_SCORE", totalScore);

  }, [
    typedFormData.WEIGHT_LOSS_ONE_MONTHS,
    typedFormData.WEIGHT_LOSS_TWO_MONTHS,
    typedFormData.WEIGHT_LOSS_TREE_MONTHS,
    typedFormData.DISEASE_INTENSITY_ONE,
    typedFormData.DISEASE_INTENSITY_TWO,
    typedFormData.DISEASE_INTENSITY_TREE,
    typedFormData.OVER_SEVENTY,
    typedFormData.NUTRITIONAL_SCORE,
    typedFormData.DISEASE_SCORE,
    typedFormData.ASSESSMENT_SCORE,
    handleChange
  ]);

  useEffect(() => {
    if (mcpCacheLoaded) {
      const details = getMCPParameterDetails(117);
      const score = Number(typedFormData.ASSESSMENT_SCORE) || 0;
      const matchingDetail = details.find((d: any) => Number(d.value) === score);
      if (matchingDetail) {
        if (typedFormData.RESULT_NOTE !== matchingDetail.name) {
          handleChange("RESULT_NOTE", matchingDetail.name);
        }
      } else if (typedFormData.RESULT_NOTE !== "") {
        handleChange("RESULT_NOTE", "");
      }
    }
  }, [typedFormData.ASSESSMENT_SCORE, mcpCacheLoaded, getMCPParameterDetails, handleChange, typedFormData.RESULT_NOTE]);

  const handleSave = async () => {
    setLocalLoading(true);
    try {
      const comId = await getComId();
      if (!comId) {
        setAlertMessage(t("card.connection-error") || "Bağlantı hatası");
        setAlertType("error");
        setAlertOpen(true);
        return;
      }
      const masterId = await getMasterId(comId);
      if (!masterId) {
        setAlertMessage(t("card.master-record-error") || "Master kayıt hatası");
        setAlertType("error");
        setAlertOpen(true);
        return;
      }

      const tableName = getTableName();
      const userFullname = `${user?.name || ""} ${user?.surname || ""}`.trim();
      const cuserValue = (user?.username || userFullname || "").substring(0, 12);
      const updatedFormData = {
        ...typedFormData,
        NRS_ID: masterId,
        PROCESS_TIME: typedFormData.PROCESS_TIME || new Date(),
        CTIME: typedFormData.CTIME || new Date(),
        CUSER: typedFormData.CUSER || cuserValue,
        NURSE_ID: typedFormData.NURSE_ID || user?.id?.toString() || ""
      };

      const columns = Object.entries(updatedFormData)
        .filter(([key]) => !['ID', 'ROWID', 'CTIME', 'RESULT_NOTE'].includes(key))
        .map(([key, value]) => ({
          key,
          value: value instanceof Date ? value.toISOString() : String(value ?? ""),
        }));

      // Grid'den bir kayıt seçiliyse veya formda ID varsa update yap
      const recordId = typedFormData.ID || selectedRecordId;

      if (!recordId) {
        setAlertMessage(t("card.please-select-a-record-first") || "Lütfen önce bir kayıt seçiniz");
        setAlertType("error");
        setAlertOpen(true);
        return;
      }

      await referenceService.card.put({
        tablename: tableName,
        id: Number(recordId),
        columns: columns as any,
      });

      setAlertMessage(t("card.record-updated-successfully"));
      setAlertType("success");
      setAlertOpen(true);
      await delay(500);
      await fetchRecords(true);
    } catch (error) {
      setAlertMessage(t("card.update-failed"));
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleDelete = useCallback(() => {
    const recordId = typedFormData.ID || selectedRecordId;
    if (!recordId) {
      setAlertMessage(t("card.record-not-found-for-delete") || "Silinecek kayıt bulunamadı");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }
    setDeleteConfirmOpen(true);
  }, [typedFormData.ID, selectedRecordId, t]);

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false);
    const recordId = typedFormData.ID || selectedRecordId;
    if (!recordId) return;

    setLocalLoading(true);
    try {
      await referenceService.card.delete({
        tablename: getTableName(),
        id: Number(recordId),
      });
      setAlertMessage(t("card.record-deleted-successfully"));
      setAlertType("success");
      setAlertOpen(true);
      resetForm();
      setSelectedRecordId(undefined);
      await delay(500);
      await fetchRecords(true);
    } catch (error) {
      setAlertMessage(t("card.delete-failed"));
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleAddAssessment = async () => {
    setLocalLoading(true);
    try {
      const comId = await getComId();
      if (!comId) return;
      const masterId = await getMasterId(comId);
      if (!masterId) return;

      // Pre-calculate values from patient data for initial record creation
      const height = Number(patientData?.data?.patlength) || 0;
      const weight = Number(patientData?.data?.weight) || 0;
      const birthdate = patientData?.data?.birthdate;

      let bmi = 0;
      let bmiScore = 0;
      if (height > 0 && weight > 0) {
        const hInMeters = height / 100;
        bmi = Number((weight / (hInMeters * hInMeters)).toFixed(2));
        bmiScore = bmi < 20.5 ? 1 : 0;
      }

      let over_seventy = 0;
      if (birthdate) {
        const age = calculateAge(birthdate);
        over_seventy = age >= 70 ? 1 : 0;
      }

      const columns = [
        { key: "NRS_ID", value: masterId },
        { key: "PROCESS_TIME", value: new Date().toISOString() },
        { key: "NURSE_ID", value: user?.id?.toString() || "" },
        { key: "CUSER", value: (user?.username || `${user?.name || ""} ${user?.surname || ""}`.trim() || "").substring(0, 12) },
        { key: "HEIGHT", value: String(height) },
        { key: "WEIGHT", value: String(weight) },
        { key: "BODY_MASS_INDEX", value: String(bmi) },
        { key: "BODY_MASS_INDEX_SCORE", value: String(bmiScore) },
        { key: "OVER_SEVENTY", value: String(over_seventy) },
      ];

      const response = await referenceService.card.post({
        tablename: "MC_NUTRITIONAL_RISK_SCORE_DET",
        columns: columns as any,
      });

      const responseAny = response as any;
      const isSuccess = responseAny.issuccess || responseAny.ID;

      if (isSuccess) {
        const newId = responseAny.ID || responseAny.data?.ID;
        setAlertMessage(t("card.record-added-successfully"));
        setAlertType("success");
        setAlertOpen(true);

        await delay(500);
        await fetchRecords(true);
        if (newId) {
          setSelectedRecordId(String(newId));
        }
      } else {
        throw new Error("Add operation failed");
      }
    } catch (error) {
      setAlertMessage(t("card.add-failed"));
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleAddEvaluation = async () => {
    if (!selectedRecordId) {
      setAlertMessage(t("card.please-select-a-record-first"));
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    setLocalLoading(true);
    try {
      const score = Number(typedFormData.ASSESSMENT_SCORE) || 0;
      const columns = [
        { key: "NRSD_ID", value: selectedRecordId },
        { key: "PROCESS_TIME", value: new Date().toISOString() },
        { key: "CTIME", value: new Date().toISOString() },
        { key: "NURSE_ID", value: user?.id?.toString() || "" },
        { key: "CUSER", value: (user?.username || `${user?.name || ""} ${user?.surname || ""}`.trim() || "").substring(0, 12) },
        { key: "ASSESSMENT_TREE_EQUAL_AND_BIG", value: score >= 3 ? "1" : "0" },
        { key: "ASSESSMENT_TREE_THEN_SMALL", value: score < 3 ? "1" : "0" },
        { key: "ASSESSMENT_NO", value: "0" },
        { key: "DIETITIAN_NOTE", value: "" },
      ];

      await referenceService.card.post({
        tablename: "MC_NUTRITIONAL_RISK_SCORE_ASS",
        columns: columns as any,
      });

      setAlertMessage(t("card.record-added-successfully"));
      setAlertType("success");
      setAlertOpen(true);

      await delay(500);
      fetchEvaluationRecords(Number(selectedRecordId));
    } catch (error) {
      console.error("Error adding evaluation:", error);
      setAlertMessage(t("card.add-failed"));
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleEditEvaluation = (evalId: number) => {
    setSelectedEvalId(evalId);
    setEditModalOpen(true);
  };

  const handleDeleteEvaluation = (evalId: number) => {
    setSelectedEvalId(evalId);
    setEvalDeleteConfirmOpen(true);
  };

  const handleDeleteEvaluationConfirm = async () => {
    if (selectedEvalId === null) return;
    setEvalDeleteConfirmOpen(false);
    setLocalLoading(true);
    try {
      await referenceService.card.delete({
        tablename: "MC_NUTRITIONAL_RISK_SCORE_ASS",
        id: selectedEvalId,
      });
      setAlertMessage(t("card.record-deleted-successfully"));
      setAlertType("success");
      setAlertOpen(true);
      await delay(500);
      if (selectedRecordId) {
        await fetchEvaluationRecords(Number(selectedRecordId));
      }
    } catch (error) {
      setAlertMessage(t("card.delete-failed"));
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalLoading(false);
      setSelectedEvalId(null);
    }
  };

  const handleConsultationRequest = async () => {
    if (!selectedRecordId) {
      setAlertMessage(t("card.please-select-a-record-first") || "Lütfen önce bir kayıt seçin");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    setLocalLoading(true);
    try {
      // Mark the record as consultation requested in the database
      await referenceService.card.put({
        tablename: "MC_NUTRITIONAL_RISK_SCORE_DET",
        id: Number(selectedRecordId),
        columns: [
          { key: "DO_CONSULTION_REQ", value: "1" }
        ]
      });

      const successMsg = t("nutritional-risk-score.consultation-request-started");
      setAlertMessage(successMsg === "nutritional-risk-score.consultation-request-started" ? "Konsültasyon süreci başlatıldı!" : successMsg);
      setAlertType("success");
      setAlertOpen(true);

      // Open the service request modal
      setOpenRequestService(true);

      // Refresh grid data to show updated flag if visible
      await fetchRecords(true);
    } catch (error) {
      console.error("Consultation request error:", error);
      setAlertMessage(t("card.update-failed") || "Kayıt güncellenirken hata oluştu");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    setSelectedRecordId(id);
    // The useCardForm hook handles loading the data into formData
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "ID",
      headerName: t("nutritional-risk-score.columns.select"),
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any): React.ReactNode => (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <ByzMuiCheckBox
            checked={selectedRecordId === String(params.id)}
            onChange={() => {
              const rowId = String(params.id);
              setSelectedRecordId(prev => prev === rowId ? undefined : rowId);
            }}
            sx={{ padding: 0 }}
          />
        </Box>
      ),
    },
    {
      field: "PROCESS_TIME",
      headerName: t("nutritional-risk-score.columns.date"),
      flex: 1,
      valueFormatter: (value: any) => formatDateTime(value)
    },
    {
      field: "CUSER",
      headerName: t("nutritional-risk-score.columns.nurse"),
      flex: 1,
      renderCell: (params: any): React.ReactNode => {
        const { row } = params;
        return row.CUSER || row.NURSE_NAME || row.NURSE_ID || "";
      },
    },
    { field: "BODY_MASS_INDEX", headerName: t("nutritional-risk-score.columns.vki"), flex: 1 },
    { field: "ASSESSMENT_SCORE", headerName: t("nutritional-risk-score.columns.score") || t("score"), flex: 0.5 },
  ], [t, selectedRecordId]);

  const columnsEvaluation: GridColDef[] = useMemo(() => [
    {
      field: "edit",
      headerName: t("card.edit"),
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any): React.ReactNode => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <ByzGrdEditButton onClick={() => handleEditEvaluation(Number(params.id))} />
        </Box>
      ),
    },
    {
      field: "PROCESS_TIME",
      headerName: t("nutritional-risk-score.columns.date"),
      flex: 1,
      valueFormatter: (value: any) => formatDateTime(value)
    },
    {
      field: "CUSER",
      headerName: t("nutritional-risk-score.columns.nurse"),
      flex: 1,
      renderCell: (params: any): React.ReactNode => {
        const { row } = params;
        return row.CUSER || row.NURSE_NAME || row.NURSE_ID || "";
      },
    },
    {
      field: "OVER_SEVENTY",
      headerName: t("nutritional-risk-score.columns.seventy"),
      flex: 1,
      renderCell: (params: any): React.ReactNode => {
        const val = params.row.OVER_SEVENTY ?? prevRecords.find(r => String(r.ID) === selectedRecordId)?.OVER_SEVENTY;
        return <ByzMuiCheckBox checked={Number(val) === 1} disabled />;
      },
    },
    {
      field: "ASSESSMENT_NO",
      headerName: t("nutritional-risk-score.columns.assessment-no") || "Gerek Yok",
      flex: 1,
      renderCell: (params: any): React.ReactNode => <ByzMuiCheckBox checked={Number(params.value) === 1} disabled />,
    },
    {
      field: "ASSESSMENT_TREE_EQUAL_AND_BIG",
      headerName: t("nutritional-risk-score.columns.assessment-high"),
      flex: 1,
      renderCell: (params: any): React.ReactNode => <ByzMuiCheckBox checked={Number(params.value) === 1} disabled />,
    },
    {
      field: "ASSESSMENT_TREE_THEN_SMALL",
      headerName: t("nutritional-risk-score.columns.assessment-low"),
      flex: 1,
      renderCell: (params: any): React.ReactNode => <ByzMuiCheckBox checked={Number(params.value) === 1} disabled />,
    },
    { field: "DIETITIAN_NOTE", headerName: t("nutritional-risk-score.columns.note"), flex: 1 },
    {
      field: "actions",
      headerName: t("nutritional-risk-score.columns.delete"),
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any): React.ReactNode => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <ByzGrdDeleteButton onClick={() => handleDeleteEvaluation(Number(params.id))} />
        </Box>
      ),
    },
  ], [t, handleDeleteEvaluation, handleEditEvaluation]);

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000, display: 'flex', flexDirection: 'column', gap: 2 }}
        open={localLoading || isInitialLoading || cardFormLoading}
      >
        <CircularProgress color="inherit" />
        {(localLoading || isInitialLoading || cardFormLoading) && <ByzMuiTypography sx={{ color: '#fff' }}>{t("card.please-wait")}</ByzMuiTypography>}
      </Backdrop>

      <Box sx={{ px: "20px", mt: 2 }}>
        <PatientDatas
          patientId={effectivePatientId}
          isCreateMedCard
          noMargin
          patientDataOut={setPatientData}
        />
      </Box>

      <Stack sx={{ px: "20px", marginBottom: "20px" }} mt={2} direction="row" spacing={2}>
        <ByzNurseSaveButton onClick={handleSave} />
        <ByzMuiAddButton onClick={handleAddAssessment} />
        <ByzNurseDeleteButton onClick={handleDelete} />
        <ByzNursePrintButton2 />
        <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: '#E0E0E0', width: '1px', height: '36px' }} />
        <ByzMuiButton variant2="outlined" onClick={handleAddEvaluation}>
          + {t("nutritional-risk-score.add-evaluation")}
        </ByzMuiButton>
        <ByzMuiButton variant2="outlined" onClick={handleConsultationRequest}>
          {t("nutritional-risk-score.consultation-request")}
        </ByzMuiButton>
      </Stack>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <PreliminaryEvaluation
          columns={columns}
          rows={prevRecords}
          onRowClick={(params: any) => setSelectedRecordId(String(params.id))}
        />

        <AssessmentForm
          formData={typedFormData}
          onChange={handleChange}
        />

        <MainAssessment
          formData={typedFormData}
          onChange={handleChange}
        />

        <EvaluationGrid
          columns={columnsEvaluation}
          rows={evaluationRecords}
        />

        <InfoText />

        <CardFooter />
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t("card.delete-confirmation")}
        content={t("card.delete-confirmation-message")}
        hideSnackbar
      />

      <ByzDeleteConfirmDialog
        open={evalDeleteConfirmOpen}
        onClose={() => setEvalDeleteConfirmOpen(false)}
        onConfirm={handleDeleteEvaluationConfirm}
        title={t("card.delete-confirmation")}
        content={t("card.delete-confirmation-message")}
        hideSnackbar
      />

      <EvaluationEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        evalId={selectedEvalId}
        nrsId={selectedRecordId}
        patientId={effectivePatientId}
        onSave={() => selectedRecordId && fetchEvaluationRecords(Number(selectedRecordId))}
      />

      <RequestServiceModal
        open={openRequestService}
        onClose={() => setOpenRequestService(false)}
        title={`${patientData?.data?.name || ""} ${patientData?.data?.surname || ""}`}
        description={patientData?.data?.deptname || ""}
        rowID={String(effectivePatientId)}
        upn={patientData?.data?.upn ? String(patientData.data.upn) : ""}
      />

      <ByzMuiSnackBar
        key={alertMessage}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
        type={alertType}
        title=""
        autoHideDuration={3000}
      />
    </>
  );
}

export default NutritionalRiskScore;
